import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  BookOpen,
  Clock,
  Users,
  Star,
  Edit,
  Settings,
  BarChart3,
} from "lucide-react";
import { eCourseService } from "../../services/elearning/eCourseService";
import type { ECourse } from "../../services/elearning/eCourseService";

const TeacherCourseView: React.FC = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const [course, setCourse] = useState<ECourse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (courseId) {
      loadCourse();
    }
  }, [courseId]);

  const loadCourse = async () => {
    try {
      setLoading(true);
      const response = await eCourseService.getCourse(courseId!);
      if (response.success) {
        setCourse(response.data);
      } else {
        setError("Failed to load course");
      }
    } catch (err) {
      console.error("Error loading course:", err);
      setError("Failed to load course");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-4xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-300 rounded w-1/3 mb-6"></div>
            <div className="h-64 bg-gray-300 rounded mb-6"></div>
            <div className="h-4 bg-gray-300 rounded w-1/2 mb-4"></div>
            <div className="h-4 bg-gray-300 rounded w-1/3"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !course) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-red-600 mb-4">{error || "Course not found"}</p>
          <button
            onClick={() => navigate("/elearning")}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Back to Courses
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate("/elearning")}
                className="flex items-center text-gray-600 hover:text-gray-900"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back to My Courses
              </button>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => navigate(`/elearning/courses/${courseId}/edit`)}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <Edit className="w-4 h-4 mr-2" />
                Edit Course
              </button>
              <button
                onClick={() =>
                  navigate(`/elearning/courses/${courseId}/manage`)
                }
                className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                <Settings className="w-4 h-4 mr-2" />
                Manage Content
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-6">
        {/* Course Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-6">
          <div className="aspect-video bg-gray-900 relative">
            {course.thumbnail ? (
              <img
                src={course.thumbnail}
                alt={course.title}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="flex items-center justify-center h-full">
                <BookOpen className="w-16 h-16 text-white opacity-50" />
              </div>
            )}
          </div>

          <div className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                  {course.title}
                </h1>
                <p className="text-lg text-gray-600">
                  {course.shortDescription || course.description}
                </p>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-gray-900">
                  {course.pricing?.type === "free"
                    ? "Free"
                    : `KES ${course.pricing?.amount?.toFixed(2)}`}
                </div>
                <div className="text-sm text-gray-500">
                  {course.status === "published" ? "Published" : "Draft"}
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-6 text-sm text-gray-600 mb-6">
              <div className="flex items-center">
                <Users className="w-4 h-4 mr-1" />
                <span>
                  {course.stats?.totalStudents || 0} students enrolled
                </span>
              </div>
              <div className="flex items-center">
                <Clock className="w-4 h-4 mr-1" />
                <span>
                  {course.duration?.estimatedHours || 0}h{" "}
                  {course.duration?.estimatedMinutes || 0}m
                </span>
              </div>
              <div className="flex items-center">
                <Star className="w-4 h-4 mr-1" />
                <span>{course.stats?.averageRating?.toFixed(1) || "0.0"}</span>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                {course.category}
              </span>
              <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm">
                {course.level}
              </span>
              <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm">
                {course.language}
              </span>
            </div>
          </div>
        </div>

        {/* Course Description */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Course Description
          </h2>
          <div className="prose max-w-none">
            <p className="text-gray-700 whitespace-pre-line">
              {course.description}
            </p>
          </div>
        </div>

        {/* Course Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 text-center">
            <div className="text-2xl font-bold text-blue-600 mb-2">
              {course.stats?.totalStudents || 0}
            </div>
            <div className="text-gray-600">Total Students</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 text-center">
            <div className="text-2xl font-bold text-green-600 mb-2">
              {course.stats?.completionRate || 0}%
            </div>
            <div className="text-gray-600">Completion Rate</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 text-center">
            <div className="text-2xl font-bold text-yellow-600 mb-2">
              {course.stats?.averageRating?.toFixed(1) || "0.0"}
            </div>
            <div className="text-gray-600">Average Rating</div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Quick Actions
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <button
              onClick={() => navigate(`/elearning/courses/${courseId}/manage`)}
              className="flex items-center justify-center p-4 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <BookOpen className="w-6 h-6 mr-3 text-blue-600" />
              <div className="text-left">
                <div className="font-medium text-gray-900">Manage Content</div>
                <div className="text-sm text-gray-600">
                  Add modules and lessons
                </div>
              </div>
            </button>
            <button
              onClick={() => navigate(`/elearning/analytics/${courseId}`)}
              className="flex items-center justify-center p-4 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <BarChart3 className="w-6 h-6 mr-3 text-green-600" />
              <div className="text-left">
                <div className="font-medium text-gray-900">View Analytics</div>
                <div className="text-sm text-gray-600">
                  Track student progress
                </div>
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeacherCourseView;
